/* global runtime */
// Bank models, global entity publishing, invoice groups, and recurring invoices; loaded before backend.jsx.

const buildInvoiceListModel = ({ year } = {}) => {
  const state = mergeState(runtime.appState);
  const activeYear = Number(year || state.activeYear) || new Date().getFullYear();
  // One payment summary per invoice for the whole list (speed track S0), see withInvoiceSummaryCache.
  const rows = withInvoiceSummaryCache(() => safeArray(state.invoices).map((invoice) => mapInvoiceToUi(invoice, state)));
  return {
    year: activeYear,
    rows,
    paymentAccounts: mapPaymentAccountsToUi(state),
    loading: runtime.loading,
    configured: runtime.configured,
    activeBusinessId: runtime.activeBusinessId,
  };
};

const bankSourceToUiMode = (source) => (source === 'private' ? 'privat' : 'drift');
const bankUiModeToSource = (mode) => (mode === 'privat' ? 'private' : 'business');
const bankLineImportRemoved = (line) => line?.reconciliationStatus === 'import-removed' || line?.importCorrectionAction === 'removed';
const bankLineReconciliationAmount = (line) => bankLineImportRemoved(line) ? 0 : num(line?.amount);
const bankStatementLineTotal = (lines = []) => safeArray(lines).reduce((sum, line) => sum + bankLineReconciliationAmount(line), 0);

const formatBankMonth = (iso) => {
  const date = new Date(nbToIso(iso));
  if (Number.isNaN(date.getTime())) return '';
  const label = date.toLocaleString('nb-NO', { month: 'long' });
  return label ? label.slice(0, 1).toUpperCase() + label.slice(1) : '';
};

const getBankLineStatus = (line, source) => {
  if (bankLineImportRemoved(line)) return 'korrigert';
  if (source === 'private') {
    if (line.classification === 'business') return 'drift';
    if (line.classification === 'ignored' || line.classification === 'private') return 'ignorer';
    return 'ny';
  }
  if (line.reconciliationStatus === 'approved') return 'godkjent';
  if (line.reconciliationStatus === 'manual-review' || line.source === 'manual') return 'avvik';
  if (line.matchedEntryId) return 'matchet';
  if (line.classification === 'private') return 'privat';
  // Mirror the authoritative Functions status: selections are drafts until a
  // persisted accounting entry is linked through matchedEntryId.
  if (line.aiSuggestedBookingType || line.aiSuggestedAccountCode || line.aiSuggestedReason) return 'forslag';
  return 'ny';
};

const getBankLineDescription = (line, status) => {
  if (status === 'korrigert') return line.importCorrectionReason ? `Linje fjernet fra avstemmingen: ${line.importCorrectionReason}` : 'Linje fjernet fra avstemmingen';
  if (status === 'avvik') return line.note || 'Lagt til manuelt og finnes ikke i importert bankutskrift';
  if (status === 'privat') return 'Privat uttak fra driftskonto';
  if (status === 'drift') return 'Flyttet til driften';
  if (status === 'ignorer') return 'Ignorert';
  if (line.note) return line.note;
  if (line.aiSuggestedReason) return line.aiSuggestedReason;
  const booking = {
    purchase: 'Kjøp',
    sale: 'Salg',
    invoice: 'Faktura',
    'invoice-payment': 'Fakturabetaling',
    'supplier-invoice-payment': 'Betal leverandørfaktura',
    'existing-entry': 'Avstemt mot eksisterende føring',
    'free-posting': 'Fri postering',
    'vat-payment': 'MVA-betaling',
    'payroll-tax': 'Forskuddstrekk/AGA',
    'tax-payment': 'Skattebetaling',
    'supplier-invoice': 'Leverandørfaktura: registrer ny',
    'other-sale': 'Annet salg',
    'owner-settlement': num(line.amount) < 0 ? 'Privat uttak/eierføring' : 'Privat innskudd/eierføring',
  }[line.bookingType || line.aiSuggestedBookingType] || '';
  const account = line.accountCode || line.aiSuggestedAccountCode || [line.fromAccountCode, line.toAccountCode].filter(Boolean).join(' -> ') || line.taxTerm || '';
  const vat = line.vatRate ?? line.aiSuggestedVatRate;
  const details = [booking, vat != null ? `${vat} % MVA` : '', account].filter(Boolean);
  return details.length ? details.join(' · ') : 'Ikke behandlet ennå';
};

const bankLineSuggestedReceiptName = (line) => {
  const receiptId = String(line?.aiSuggestedReceiptInboxEntryId || '').trim();
  if (!receiptId) return '';
  const receipt = safeArray(runtime.appState?.receiptInbox).find((entry) => entry?.id === receiptId);
  return receipt?.attachmentName || receipt?.name || receipt?.fileName || '';
};

const bankLineReceiptRefs = (line = {}) => {
  const refs = [];
  safeArray(line.receiptAttachments).forEach((entry) => {
    const receiptId = String(entry?.receiptInboxEntryId || entry?.id || '').trim();
    if (!receiptId) return;
    refs.push(stripUndefinedDeep({
      receiptInboxEntryId: receiptId,
      attachmentName: entry.attachmentName || entry.name || 'Bilag',
      attachmentId: entry.attachmentId || '',
      attachmentMimeType: entry.attachmentMimeType || '',
      attachmentDownloadUrl: entry.attachmentDownloadUrl || entry.downloadUrl || entry.file || '',
      documentRole: entry.documentRole || '',
    }));
  });
  const legacyId = String(line.receiptInboxEntryId || line.aiSuggestedReceiptInboxEntryId || '').trim();
  if (legacyId && !refs.some((entry) => entry.receiptInboxEntryId === legacyId)) refs.unshift(stripUndefinedDeep({
    receiptInboxEntryId: legacyId,
    attachmentName: line.attachmentName || 'Bilag',
    attachmentId: line.attachmentId || '',
    attachmentMimeType: line.attachmentMimeType || '',
    attachmentDownloadUrl: line.attachmentDownloadUrl || '',
  }));
  return refs;
};

// Keep receipt requirements consistent across bank lines, flags, and inbox cleanup.
const bankLineNeedsReceipt = (line = {}) => num(line.amount) < 0 && !bankLineReceiptRefs(line).some((entry) => entry.documentRole !== 'payment_confirmation')
  && !line.attachmentName && !line.receiptWaived && !['bank-statement', 'waived'].includes(line.documentationMode);

const mapBankLineToUi = (line, source, statementId = '', approvals = {}) => {
  const taskId = statementId && line?.id ? `bank:${statementId}:${line.id}` : '';
  const derivedStatus = getBankLineStatus(line, source);
  const approvalCanDriveStatus = !['forslag', 'kontroll', 'ny'].includes(derivedStatus);
  const approved = Boolean(taskId && approvals?.[taskId] && approvalCanDriveStatus);
  const reviewStatus = derivedStatus === 'forslag' && (line.aiSuggestedClassification === 'review' || line.aiSuggestionConfidence !== 'high') ? 'kontroll' : derivedStatus;
  const approvedButNotBooked = source !== 'private'
    && line?.reconciliationStatus === 'approved'
    && line?.classification === 'business'
    && !line?.matchedEntryId;
  const status = derivedStatus === 'avvik'
    ? 'avvik'
    : line?.matchedEntryId
    ? (line.bookingType === 'existing-entry' ? 'matchet' : 'bokfort')
    : approvedButNotBooked ? 'kontroll'
    : approved ? 'godkjent' : reviewStatus;
  const suggestedReceiptName = bankLineSuggestedReceiptName(line);
  const receiptAttachments = bankLineReceiptRefs(line);
  const primaryReceipt = receiptAttachments.find((entry) => entry.documentRole !== 'payment_confirmation') || {};
  return {
    id: line.id,
    date: isoToNb(line.date).slice(0, 5) || line.date || '',
    isoDate: line.date || todayIso(),
    title: line.description || 'Banklinje',
    sub: getBankLineDescription(line, status),
    amount: num(line.amount),
    reconciliationAmount: bankLineReconciliationAmount(line),
    status,
    forslag: line.aiSuggestedReason || getBankLineDescription({ ...line, bookingType: line.aiSuggestedBookingType, accountCode: line.aiSuggestedAccountCode, vatRate: line.aiSuggestedVatRate }, status),
    bilag: bankLineNeedsReceipt(line) && !['privat', 'korrigert'].includes(status),
    bilagName: primaryReceipt.attachmentName || line.attachmentName || '',
    receiptAttachments,
    suggestedBilagName: suggestedReceiptName,
    receiptInboxEntryId: primaryReceipt.receiptInboxEntryId || line.receiptInboxEntryId || '',
    approvalTaskId: taskId,
    approved,
    _backend: line,
  };
};

const mapBankStatementToUi = (statement, approvals = {}, suppliedState) => {
  const state = suppliedState || mergeState(runtime.appState);
  const reviewedStatement = statement.rowWindowIncomplete ? statement : bankStatementTransitionForUi(state, statement);
  const source = reviewedStatement.source || 'business';
  const lines = safeArray(reviewedStatement.lines).map((line) => mapBankLineToUi(line, source, reviewedStatement.id, approvals));
  const handled = lines.filter((line) => ['godkjent', 'matchet', 'bokfort', 'privat', 'drift', 'ignorer'].includes(line.status)).length;
  return {
    id: reviewedStatement.id,
    mode: bankSourceToUiMode(source),
    source,
    bankAccountId: reviewedStatement.bankAccountId || '',
    fileName: reviewedStatement.fileName || reviewedStatement.originalFileName || 'Bankutskrift',
    month: formatBankMonth(reviewedStatement.periodFrom || reviewedStatement.importedAt),
    period: [isoToNb(reviewedStatement.periodFrom), isoToNb(reviewedStatement.periodTo)].filter(Boolean).join(' - '),
    periodFrom: reviewedStatement.periodFrom || todayIso(),
    periodTo: reviewedStatement.periodTo || reviewedStatement.periodFrom || todayIso(),
    lines: reviewedStatement.rowSummary?.count ?? lines.length,
    handled: reviewedStatement.rowSummary?.handled ?? handled,
    date: isoToNb(reviewedStatement.importedAt || reviewedStatement.periodFrom),
    status: reviewedStatement.status || 'active',
    lineItems: lines,
    _backend: reviewedStatement,
  };
};

const mapReceiptToBankInboxUi = (entry) => ({
  id: entry.id,
  name: entry.attachmentName || 'Bilag',
  // Datoen paa bilaget, ikke datoen det ble lastet opp: en bruker kan laste opp
  // hele aaret i desember, og det er kjopsdatoen som moter banklinja. Opplastingen
  // er fallback, for et bilag uten avlest dato har ingen annen tidfesting.
  meta: [isoToNb(entry.receiptAnalysis?.receiptDate) || isoToNb(entry.createdAt), entry.receiptAnalysis?.supplierName || entry.source].filter(Boolean).join(' · '),
  // NOK-belopet, ikke originalbelopet: dette tallet stilles opp mot banklinjer i
  // kroner, og 125 USD ville ellers blitt lest som 125 kr.
  amount: num(window.PlummeReceiptCurrency.grossNok(entry.receiptAnalysis)),
  match: false,
  _backend: entry,
});

const bankPostingBankDirectionAmount = (posting) => /^19[2-9]\d$/.test(String(posting?.creditAccountCode || ''))
  ? -Math.abs(num(posting?.amount))
  : Math.abs(num(posting?.amount));
const bankPostingAccountCode = (posting) => [posting?.creditAccountCode, posting?.debitAccountCode].find(isBankLedgerAccountCode) || '';

const mapBankPostingToUi = (posting) => ({
  id: posting.id,
  entryType: posting.entryType || posting.matchedEntryType || 'bank-posting',
  date: isoToNb(posting.date),
  title: posting.description || 'Bankpostering',
  sub: [posting.debitAccountCode, posting.creditAccountCode].filter(Boolean).join(' → '),
  amount: bankPostingBankDirectionAmount(posting),
  pos: bankPostingBankDirectionAmount(posting) > 0,
  status: posting.status === 'avvik' ? 'avvik' : 'matchet',
  note: posting.bankTrace?.lineDescription ? `Stemmer mot banklinje ${isoToNb(posting.bankTrace.lineDate)}: ${posting.bankTrace.lineDescription}` : '',
  statementId: posting.bankTrace?.statementId || '',
  lineId: posting.bankTrace?.lineId || '',
  statementIds: safeArray(posting.bankTraces).map((trace) => trace?.statementId).filter(Boolean),
  lineIds: safeArray(posting.bankTraces).map((trace) => trace?.lineId).filter(Boolean),
  bankMatchGroupId: posting.bankMatchGroupId || '',
  bankAccountCode: bankPostingAccountCode(posting),
  // Motkontoen er den siden som ikke er bankkontoen. Modalen retter bare den.
  counterAccountCode: /^19[2-9]\d$/.test(String(posting?.creditAccountCode || '')) ? posting.debitAccountCode || '' : posting.creditAccountCode || '',
  vatRate: posting.vatRate == null ? null : Number(posting.vatRate),
  attachmentName: posting.attachmentName || '',
  receiptInboxEntryId: posting.receiptInboxEntryId || '',
  _backend: posting,
});

// Hvilke bilag som allerede henger paa en banklinje, uansett hvilken koblingsvei som ble brukt.
const bankLinkedReceiptIndex = (state) => {
  const index = new Map();
  safeArray(state?.bankStatements).forEach((statement) => {
    const bankAccount = safeArray(state?.settings?.bankAccounts).find((account) => String(account?.id || '') === String(statement?.bankAccountId || ''));
    const accountCode = String(bankAccount?.ledgerAccountCode || bankAccount?.ledger || bankAccount?.accountCode || '').trim();
    safeArray(statement?.lines).forEach((line) => {
      bankLineReceiptRefs(line).forEach((receiptRef) => {
        const receiptId = String(receiptRef?.receiptInboxEntryId || '');
        if (!receiptId) return;
        index.set(receiptId, {
          statementId: statement.id, lineId: line.id, accountCode, bankAccountId: statement.bankAccountId || '',
          lineDescription: line.description || '', lineAmount: num(line.amount),
          suggested: !line?.receiptInboxEntryId, booked: Boolean(line.matchedEntryId),
        });
      });
    });
  });
  return index;
};
const bankLinkedReceiptPaymentSourceHint = (link = {}) => link?.accountCode ? { paymentSource: 'company', accountCode: link.accountCode, confidence: 'high', origin: 'bank-match', match: { confidence: 'high', statementId: link.statementId, lineId: link.lineId }, requiresReview: false, reason: 'Bilaget er koblet til en banklinje på bedriftskontoen.' } : null;

const isBankLedgerAccountCode = (code) => /^19[2-9]\d$/.test(String(code || ''));

// Include settled purchases so importing bank data cannot book the same purchase twice.
const bankSettledEntryPosts = (state) => {
  const tracedEntryIds = new Set();
  const expenseById = new Map(safeArray(state?.expenses).map((expense) => [String(expense?.id || ''), expense]));
  const entryBankTraceByEntryId = new Map();
  const receiptBankTraceByReceiptId = new Map();
  safeArray(state?.bankStatements).forEach((statement) => {
    safeArray(statement?.lines).forEach((line) => {
      const entryId = String(line?.matchedEntryId || '');
      const trace = {
        statementId: statement.id,
        lineId: line.id,
        lineDate: line.date,
        lineDescription: line.description,
        lineAmount: num(line.amount),
      };
      if (entryId) {
        tracedEntryIds.add(entryId);
        if (!entryBankTraceByEntryId.has(entryId)) entryBankTraceByEntryId.set(entryId, trace);
      }
      const receiptId = String(line?.receiptInboxEntryId || line?.aiSuggestedReceiptInboxEntryId || '').trim();
      if (receiptId && !receiptBankTraceByReceiptId.has(receiptId)) {
        receiptBankTraceByReceiptId.set(receiptId, trace);
      }
    });
  });

  const posts = [];
  const pushManualBankLine = (expense, line, index, side) => {
    const accountCode = String(side === 'from' ? line?.fromAccount : line?.toAccount || '').trim();
    if (!isBankLedgerAccountCode(accountCode)) return;
    const amount = side === 'from' ? -Math.abs(num(line?.fromAmount)) : Math.abs(num(line?.toAmount));
    if (Math.abs(amount) <= 0.005) return;
    const trace = expense?.bankTrace || line?.bankTrace || entryBankTraceByEntryId.get(String(expense?.id || '')) || null; // Koblet banklinje avstemmer posteringen ogsaa uten eget bankspor.
    posts.push({
      id: `${expense.id}-manual-bank-${index}-${side}`,
      entryType: 'manual-posting',
      date: isoToNb(expense.date || expense.payDate || expense.createdAt),
      isoDate: String(expense.date || expense.payDate || expense.createdAt || '').slice(0, 10),
      title: expense.description || 'Fri postering',
      sub: [`Bokført på ${accountCode}`, side === 'from' ? line?.fromAccountLabel : line?.toAccountLabel].filter(Boolean).join(' · '),
      amount,
      pos: amount > 0,
      status: trace?.lineId ? 'matchet' : 'avvik',
      note: trace?.lineDescription ? `Stemmer mot banklinje ${isoToNb(trace.lineDate)}: ${trace.lineDescription}` : 'Mulig avvik: bokført på bankkontoen, men ikke funnet i bankutskriften.',
      statementId: trace?.statementId || '',
      lineId: trace?.lineId || '',
      bankAccountCode: accountCode,
      counterAccountCode: String(side === 'from' ? line?.toAccount : line?.fromAccount || '').trim(),
      vatRate: null,
      attachmentName: expense.attachmentName || '',
      receiptInboxEntryId: expense.receiptInboxEntryId || '',
      _backend: expense,
    });
  };
  const pushPostedBankEvent = ({ id, entryType, date, title, sub, postings, note }) => {
    if (!id || tracedEntryIds.has(String(id))) return;
    const bankRows = safeArray(postings).filter((line) => isBankLedgerAccountCode(line?.accountCode || line?.nr));
    if (bankRows.length !== 1) return;
    const bankAmount = num(bankRows[0].amount);
    const accountCode = bankRows[0].accountCode || bankRows[0].nr || '';
    if (Math.abs(bankAmount) <= 0.005) return;
    posts.push({
      id,
      entryType,
      date: isoToNb(date),
      isoDate: String(date || '').slice(0, 10),
      title,
      sub: [sub, `oppgjort mot ${bankRows[0].accountCode || bankRows[0].nr}`].filter(Boolean).join(' · '),
      amount: bankAmount,
      pos: bankAmount > 0,
      status: 'avvik',
      note: note ? `Mulig avvik: ${note}` : 'Mulig avvik: bokført på bankkontoen, men ikke funnet i bankutskriften.',
      statementId: '',
      bankAccountCode: accountCode,
      counterAccountCode: '',
      vatRate: null,
      attachmentName: '',
      receiptInboxEntryId: '',
    });
  };
  safeArray(state?.expenses).forEach((expense) => {
    if (!isActiveExpense(expense)) return;
    if (expense.manualPosting) {
      safeArray(expense.manualPostingLines).forEach((line, index) => {
        pushManualBankLine(expense, line, index, 'from');
        pushManualBankLine(expense, line, index, 'to');
      });
      return;
    }
    // Leverandorfakturaen bokfores mot 2400. Selve oppgjoret finnes som en
    // separat PaymentEntry og tas med i loekken nedenfor.
    if (expense.isSupplierInvoice) return;
    const settlement = expenseSettlementAccount(expense);
    if (!isBankLedgerAccountCode(settlement)) return;
    // Ubetalt leverandorfaktura har ingen bankbevegelse aa avstemme ennaa.
    if (expense.isPaid === false) return;
    const gross = Math.abs(num(dashboardExpenseVatBreakdown(expense).grossAmount));
    if (!gross) return;
    const bankTrace = expense.bankTrace
      || entryBankTraceByEntryId.get(String(expense.id || ''))
      || receiptBankTraceByReceiptId.get(String(expense.receiptInboxEntryId || '').trim())
      || null;
    posts.push({
      id: expense.id,
      entryType: 'expense',
      date: isoToNb(expense.paidAt || expense.payDate || expense.date),
      isoDate: String(expense.paidAt || expense.payDate || expense.date || '').slice(0, 10),
      title: expense.description || expense.supplierName || 'Bokført kjøp',
      sub: [expense.supplierName, `oppgjort mot ${settlement}`].filter(Boolean).join(' · '),
      amount: -gross,
      pos: false,
      status: bankTrace?.lineId ? 'matchet' : 'avvik',
      note: bankTrace?.lineDescription ? `Bokført fra banklinje ${isoToNb(bankTrace.lineDate)}.` : 'Mulig avvik: bokført bilag på bankkontoen, men ikke funnet i bankutskriften.',
      statementId: bankTrace?.statementId || '',
      lineId: bankTrace?.lineId || '',
      bankAccountCode: settlement,
      counterAccountCode: '',
      vatRate: null,
      attachmentName: expense.attachmentName || '',
      receiptInboxEntryId: expense.receiptInboxEntryId || '',
      _backend: expense,
    });
  });
  safeArray(state?.otherSales).forEach((sale) => {
    if (!isActiveOtherSale(sale)) return;
    const saleId = String(sale?.id || '').trim();
    if (!saleId) return;
    const bankTrace = sale?.bankTrace || entryBankTraceByEntryId.get(saleId) || null;
    const postings = safeArray(otherSalePostings(sale));
    const salesRow = postings.find((line) => num(line?.amount) < 0 && !isBankLedgerAccountCode(line?.accountCode || line?.nr));
    const bankRows = postings.filter((line) => isBankLedgerAccountCode(line?.accountCode || line?.nr));
    bankRows.forEach((bankRow, index) => {
      const bankAmount = num(bankRow.amount);
      if (Math.abs(bankAmount) <= 0.005) return;
      const accountCode = String(bankRow.accountCode || bankRow.nr || '').trim();
      posts.push({
        id: bankRows.length === 1 ? saleId : `${saleId}-bank-${index + 1}`,
        entryType: 'other-sale',
        date: isoToNb(sale.date || sale.createdAt),
        isoDate: String(sale.date || sale.createdAt || '').slice(0, 10),
        title: sale.description || 'Annet salg',
        sub: [`${sale.voucherNumber || saleId}`, `oppgjort mot ${accountCode}`].filter(Boolean).join(' · '),
        amount: bankAmount,
        pos: bankAmount > 0,
        status: bankTrace?.lineId ? 'matchet' : 'avvik',
        note: bankTrace?.lineDescription ? `Bokført fra banklinje ${isoToNb(bankTrace.lineDate)}.` : 'Mulig avvik: bokført annet salg på bankkontoen, men ikke funnet i bankutskriften.',
        statementId: bankTrace?.statementId || '',
        lineId: bankTrace?.lineId || '',
        bankAccountCode: accountCode,
        counterAccountCode: sale.salesAccountCode || salesRow?.accountCode || salesRow?.nr || '',
        vatRate: sale.vatTreatment === 'standard' ? num(sale.vatRate) : 0,
        attachmentName: sale.attachmentName || safeArray(sale.attachments)[0]?.name || '',
        receiptInboxEntryId: sale.receiptInboxEntryId || '',
        _backend: sale,
      });
    });
  });
  safeArray(state?.payments).forEach((payment) => {
    if (!payment || payment.status === 'reversed' || payment.settlementKind === 'no_payment') return;
    const documentExpense = expenseById.get(String(payment.documentId || ''));
    const bankTrace = payment.bankTrace
      || (payment.documentType === 'supplier_invoice' ? documentExpense?.bankTrace : null)
      || (payment.documentType === 'supplier_invoice' ? receiptBankTraceByReceiptId.get(String(documentExpense?.receiptInboxEntryId || '').trim()) : null)
      || receiptBankTraceByReceiptId.get(String(payment.receiptInboxEntryId || '').trim())
      || null;
    if (tracedEntryIds.has(String(payment.id || '')) && !bankTrace) return;
    const settlement = payment.settlementAccountCode || '1920';
    if (!isBankLedgerAccountCode(settlement)) return;
    const outgoing = payment.direction === 'outgoing';
    if (!outgoing && payment.direction !== 'incoming') return;
    const feeAmount = Math.max(0, num(payment.feeAmount));
    const bankAmount = payment.bankAmount === undefined || payment.bankAmount === null || payment.bankAmount === ''
      ? (outgoing ? num(payment.amount) + feeAmount : num(payment.amount) - feeAmount)
      : num(payment.bankAmount);
    if (!bankAmount) return;
    posts.push({
      id: payment.id,
      entryType: 'payment',
      date: isoToNb(payment.date),
      isoDate: String(payment.date || '').slice(0, 10),
      title: payment.reference || (outgoing ? 'Betaling' : 'Innbetaling'),
      sub: [payment.documentType === 'supplier_invoice' ? 'Betalt leverandørfaktura' : 'Fakturabetaling', `oppgjort mot ${settlement}`].filter(Boolean).join(' · '),
      amount: outgoing ? -Math.abs(bankAmount) : Math.abs(bankAmount),
      pos: !outgoing,
      status: bankTrace?.lineId ? 'matchet' : 'avvik',
      note: bankTrace?.lineDescription ? `Bokført fra banklinje ${isoToNb(bankTrace.lineDate)}.` : 'Mulig avvik: bokført betaling på bankkontoen, men ikke funnet i bankutskriften.',
      statementId: bankTrace?.statementId || '',
      lineId: bankTrace?.lineId || '',
      bankAccountCode: settlement,
      counterAccountCode: payment.documentType === 'supplier_invoice' ? '2400' : '1500',
      vatRate: null,
      attachmentName: '',
      receiptInboxEntryId: payment.receiptInboxEntryId || '',
      _backend: payment,
    });
  });
  safeArray(state?.payrollRuns).forEach((run) => {
    payrollRunPaymentEvents(run, state).forEach((event) => pushPostedBankEvent({
      id: event.id,
      entryType: 'payroll-payment',
      date: event.date,
      title: event.description,
      sub: run.period ? `Loenn ${run.period}` : 'Loenn',
      postings: event.postings,
      note: 'Bokfoert loenns- eller OTP-betaling. Match banklinjen mot denne posten i stedet for aa bokfoere paa nytt.',
    }));
  });
  Object.values(state?.payrollTaxPaymentsByYear || {}).forEach((entries) => {
    safeArray(entries).filter((payment) => payment?.status !== 'reversed').forEach((payment) => pushPostedBankEvent({
      id: payment.id,
      entryType: 'payroll-tax-payment',
      date: payment.date,
      title: payment.note || (payment.kind === 'withholding' ? 'Betaling forskuddstrekk' : 'Betaling arbeidsgiveravgift'),
      sub: payment.kind === 'withholding' ? 'Forskuddstrekk' : 'Arbeidsgiveravgift',
      postings: payrollTaxPaymentPostings(state, payment),
      note: 'Bokfoert betaling fra loennsmodulen. Match banklinjen mot denne posten i stedet for aa bokfoere paa nytt.',
    }));
  });
  Object.values(state?.deadlinesByYear || {}).forEach((deadlineStatus) => {
    Object.values(deadlineStatus?.mvaPaymentRecords || {}).forEach((records) => {
      safeArray(records).filter((record) => record?.status !== 'reversed').forEach((record) => {
        const year = Number(String(record?.date || '').slice(0, 4)) || Number(state?.activeYear);
        pushPostedBankEvent({
          id: record.id,
          entryType: 'vat-payment',
          date: record.date,
          title: record.settlementKind === 'refund' ? 'MVA-tilbakebetaling' : 'MVA-betaling',
          sub: 'MVA-oppgjoer',
          postings: vatPaymentYearEndLines(state, record).map((line) => posting(
            line.accountCode,
            plumme1AccountName(line.accountCode, `Konto ${line.accountCode}`),
            'MVA-betaling',
            line.signedAmount,
          )),
          note: `Bokfoert MVA-betaling for ${year || 'valgt aar'}. Match banklinjen mot denne posten i stedet for aa bokfoere paa nytt.`,
        });
      });
    });
  });
  return posts;
};

const buildBankPageModel = () => {
  const state = mergeState(runtime.appState);
  const linked = bankLinkedReceiptIndex(state);
  const duplicateIndex = buildReceiptUploadDuplicateIndex(receiptInboxBackendEntries());
  const inboxEntriesById = new Map(safeArray(getReceiptInboxModel()?.entries).map((item) => [String(item.id), item]));
  return {
    accounts: mapBankAccountsToUi(state), bankControl: yearClosingBankControl(state, Number(state.activeYear)),
    statements: safeArray(state.bankStatements).map((statement) => mapBankStatementToUi(statement, state.autopilotTaskApprovals, state)),
    // Et bilag i papirkurven skal ikke kunne kobles til en banklinje. Et koblet
    // bilag skal derimot bli staaende og vaere merket — det er dokumentasjonen for
    // en linje som ikke er bokfort ennaa.
    inbox: safeArray(state.receiptInbox)
      .filter((entry) => !receiptInboxEntryIsTrashed(entry))
      .filter((entry) => entry.status !== 'booked' || linked.has(entry.id))
      .map((entry) => {
        const linkedBankLine = linked.get(entry.id);
        const paymentSourceHint = bankLinkedReceiptPaymentSourceHint(linkedBankLine) || window.PlummeReceiptPaymentSource.suggest(state, entry.receiptAnalysis);
        // The cached inbox model is the source of truth for a receipt's review state.
        const inboxEntry = inboxEntriesById.get(String(entry.id));
        const review = inboxEntry?.review || receiptInboxReview(entry, paymentSourceHint);
        return {
          ...mapReceiptToBankInboxUi(entry),
          linkedBankLine,
          review,
          bankLinkedResolved: inboxEntry
            ? inboxEntry.bankLinkedResolved === true
            : receiptBankLinkedResolved(linkedBankLine, review),
          paymentSourceHint,
          duplicateUpload: receiptUploadDuplicateFor(entry, duplicateIndex),
        };
      }),
    postings: [
      ...safeArray(state.bankReconciliationPostings).filter((entry) => entry?.status !== 'annulled').map(mapBankPostingToUi),
      ...bankSettledEntryPosts(state),
    ],
  };
}; let lastPublishedGlobalState = null; let globalsPublished = false;
const publishGlobals = (state) => {
  const sourceState=state||null;if(globalsPublished&&sourceState===lastPublishedGlobalState)return;globalsPublished=true;lastPublishedGlobalState=sourceState;state=state||readUiDefaultState();const publishStartedAt=typeof performance!=='undefined'?performance.now():0;
  const year = Number(state.activeYear) || new Date().getFullYear(); const { once } = window.PlummeLazyGlobals;
  // Every read model is derived on first read, not here (see lazyGlobals.js). The three
  // report parts are separate so a screen that reads CHART never pays for TRIAL_BALANCE.
  const company = once(() => buildCompany(state)); const activeBusinessId = window.PlummeBackend?.activeBusinessId || 'active';
  const customers = once(() => listPartiesForDisplay(state.customers).map((customer) => mapCustomerToUi(customer, state)));
  const suppliers = once(() => listPartiesForDisplay(state.suppliers).map((supplier) => mapSupplierToUi(supplier, state)));
  const products = once(() => safeArray(state.products).map(mapProductToUi));
  const documentTextLines = safeArray(state.documentTextLines);
  const invoices = once(() => withInvoiceSummaryCache(() => safeArray(state.invoices).map((invoice) => mapInvoiceToUi(invoice, state))));
  const inbox = once(() => safeArray(state.receiptInbox).map(mapReceiptToUi));
  const mileage = once(() => safeArray(state.mileageEntries).map(mapMileageToUi));
  const payroll = once(() => derivePayroll(state));
  const accountingReportsBlocked = window.PlummeAccountingBasisCoverage.isHydratedStateBlocked(state);
  const reportPart = (part) => once(() => accountingReportsBlocked
    ? null
    : deriveReports(state, year, { includeChart: part === 'chart', includeResult: part === 'result', includeTrial: part === 'trial' })[part]); const payrollSettings = state.payrollSettings;

  window.PlummeLazyGlobals.publish({
    COMPANY: company,
    CLIENT_COMPANY: () => ({ [activeBusinessId]: company() }),
    companyFor: () => () => company(),
    CUSTOMERS: customers,
    SUPPLIERS: suppliers,
    PRODUCTS: products,
    DOCUMENT_TEXT_LINES: () => documentTextLines,
    INVOICES: invoices,
    VOUCHERS: () => deriveVouchers(state),
    INBOX: inbox,
    MILEAGE: mileage,
    VAT: () => deriveVat(state, year),
    CHART_ACCOUNTS: () => mergePlumme1ChartAccounts(state.chartAccounts, state.settings),
    CHART: reportPart('chart'),
    RESULT: reportPart('result'),
    TRIAL_BALANCE: reportPart('trial'),
    ACCOUNTING_REPORTS_BLOCKED: () => accountingReportsBlocked,
    ACCOUNTING_BASIS_COVERAGE: () => state.accountingBasisCoverage || null,
    EMPLOYEES: () => payroll().employees,
    PAY_OVERVIEW: () => payroll().overview,
    PAY_RUNS: () => payroll().runs,
    PAY_TERMS: () => payroll().terms,
    PAY_HOLIDAY: () => payroll().holiday,
    PAY_PENSION: () => payroll().pension,
    PAY_FIXED_LINES: () => payroll().fixedLines,
    PAY_TYPES: () => payroll().payTypes,
    PAY_READY_LINES: () => payroll().readyLines,
    PAY_TRAVELS: () => payroll().travels,
    PAY_SETTINGS: () => ({
      firstYear: payrollSettings.firstPayrollYear || state.activeYear || new Date().getFullYear(),
      orgNumber: payrollSettings.businessNumber || state.settings?.orgNumber || '',
      holidayMonth: payrollSettings.holidayPayMonth || 'june',
      workTimeScheme: payrollSettings.workSchedule || 'none',
      fullWeekHours: payrollSettings.weeklyHours || 37.5,
      employmentType: payrollSettings.employmentType || 'ordinary',
      payPeriod: payrollSettings.payrollPeriod || 'month',
      municipalityCode: payrollSettings.municipalityCode || '0301',
      municipalityName: payrollSettings.municipalityName || 'Oslo',
      agaCode: payrollSettings.calculationCode || 'general',
      agaZone: payrollSettings.employerContributionZone || '',
      pensionProvider: payrollSettings.pensionProviderName || '',
      pensionOrg: payrollSettings.pensionProviderOrgNumber || '',
      holidayRate: `${payrollSettings.holidayPayPercent ?? 10.2} %`,
      agaSone: `${payrollSettings.employerContributionRate ?? 14.1} %`,
      otpRate: payrollSettings.pensionContributionPercent ? `${payrollSettings.pensionContributionPercent} %` : '',
      salaryAccount: payrollSettings.payrollBankAccountId || '',
      taxAccount: payrollSettings.taxWithholdingBankAccountId || '',
      bankAccounts: mapBankAccountsToUi(state),
      taxCardEmail: payrollSettings.taxCardNotificationEmail || '',
      taxCardSms: payrollSettings.taxCardNotificationSms || '',
      reportingFreq: payrollSettings.reportingFrequency || 'monthly',
    }),
    // Reads the globals, not the local thunks, so an assignment such as
    // `window.EMPLOYEES = rows` is reflected here as it was before.
    clientStoreCache: () => ({
      products: window.PRODUCTS,
      documentTextLines,
      customers: window.CUSTOMERS,
      suppliers: window.SUPPLIERS,
      invoices: window.INVOICES,
      drafts: window.INVOICES.filter((invoice) => invoice.status === 'utkast'),
      inbox: inbox(),
      mileage: window.MILEAGE,
      employees: window.EMPLOYEES,
      travels: window.PAY_TRAVELS,
      bgByPage: state.bgByPage && typeof state.bgByPage === 'object' && !Array.isArray(state.bgByPage)
        ? { ...state.bgByPage }
        : {},
    }),
  }); window.PlummeStartupPerformance?.mark(sourceState?'globals-state-published':'globals-default-published',{durationMs:publishStartedAt?Math.round((performance.now()-publishStartedAt)*10)/10:null});
};

const mapRows = (list, rows, map) => {
  const byId = new Map(safeArray(list).map((entry) => [entry.id, entry]));
  return rows.map((row) => map(row, byId.get(row.id)));
};

const setEntity = (key, rows) => {
  if (key === 'employees') {
    void updateStateAndPersist((state) => {
      const existing = new Map(safeArray(state.employees).map((entry) => [entry.id, entry]));
      return { ...state, employees: rows.map((row) => mapUiEmployeeToBackend(row, existing.get(row.id))) };
    }).catch((error) => { runtime.error = error.message; emitRuntime(); });
    return;
  }
  if (key === 'mileage' && runtime.configured) {
    const state = mergeState(runtime.appState);
    return mutateSettingsData({ action: 'replace_mileage', resource: 'mileage',
      rows: mapRows(state.mileageEntries, rows, mapUiMileageToBackend) });
  }
  updateState((state) => {
    if (key === 'bgByPage') {
      return {
        ...state,
        bgByPage: rows && typeof rows === 'object' && !Array.isArray(rows) ? { ...rows } : {},
      };
    }
    if (key === 'products') {
      return { ...state, products: mapRows(state.products, rows, mapUiProductToBackend) };
    }
    if (key === 'documentTextLines') {
      return { ...state, documentTextLines: safeArray(rows) };
    }
    if (key === 'customers') {
      return { ...state, customers: mapRows(state.customers, rows, mapUiCustomerToBackend) };
    }
    if (key === 'suppliers') {
      return { ...state, suppliers: assignSupplierNumbers(mapRows(state.suppliers, rows, mapUiSupplierToBackend)) };
    }
    if (key === 'inbox') {
      return { ...state, receiptInbox: mapRows(state.receiptInbox, rows, mapUiReceiptToBackend) };
    }
    if (key === 'mileage') {
      return { ...state, mileageEntries: mapRows(state.mileageEntries, rows, mapUiMileageToBackend) };
    }

    return state;
  });
  const event = {
    products: BACKEND_EVENTS.products,
    documentTextLines: BACKEND_EVENTS.documentTextLines,
    customers: BACKEND_EVENTS.customers,
    suppliers: BACKEND_EVENTS.suppliers,
    inbox: BACKEND_EVENTS.inbox,
    mileage: BACKEND_EVENTS.mileage,
    employees: BACKEND_EVENTS.employees,
  }[key];
  if (event) window.dispatchEvent(new Event(event));
};

const getCustomerArchiveInfo = (customerId) => {
  const state = mergeState(runtime.appState);
  const activeGroupMemberships = safeArray(state.customerBillingGroupMembers)
    .filter((member) => member.customerId === customerId && !member.removedAt);
  const activeSubscriptions = safeArray(state.customerSubscriptions)
    .filter((subscription) => subscription.customerId === customerId && subscription.status !== 'archived' && subscription.status !== 'ended');
  return {
    groupMembershipCount: activeGroupMemberships.length,
    subscriptionCount: activeSubscriptions.length,
  };
};

const normalizeCustomerBillingGroup = (group = {}, existing = {}) => {
  const now = nowIso();
  const type = group.type === 'list' ? 'list' : group.type === 'recurring' ? 'recurring' : 'subscription';
  const interval = ['weekly', 'fourteen_days', 'monthly', 'quarterly', 'semiannual', 'annual'].includes(group.interval)
    ? group.interval
    : group.period === 'Kvartalsvis'
      ? 'quarterly'
      : group.period === 'Halv\u00e5rlig'
        ? 'semiannual'
        : group.period === '\u00c5rlig'
          ? 'annual'
          : 'monthly';
  return stripUndefinedDeep({
    ...existing,
    ...group,
    id: String(group.id || existing.id || createId('billing-group')),
    type,
    name: String(group.name || existing.name || '').trim(),
    description: String(group.description ?? group.desc ?? existing.description ?? '').trim(),
    internalComment: String(group.internalComment ?? group.note ?? existing.internalComment ?? '').trim(),
    color: String(group.color || existing.color || '#8E4EC6'),
    unitPrice: type === 'subscription' ? Math.max(0, num(group.unitPrice ?? group.price ?? existing.unitPrice)) : undefined,
    vatRate: type === 'subscription' ? num(group.vatRate ?? group.vat ?? existing.vatRate, standardVatForCountry()) : undefined,
    interval: type !== 'list' ? interval : undefined,
    dueDays: type !== 'list' ? Math.max(0, Math.round(num(group.dueDays ?? group.due ?? existing.dueDays, 14))) : undefined,
    cpiAdjustmentEnabled: type === 'subscription' ? Boolean(group.cpiAdjustmentEnabled ?? existing.cpiAdjustmentEnabled) : undefined,
    cpiAdjustmentRate: type === 'subscription' ? Math.max(0, Math.round(num(group.cpiAdjustmentRate ?? group.kpiAdjustmentRate ?? existing.cpiAdjustmentRate) * 100) / 100) : undefined,
    startDate: type !== 'list' ? nbToIso(group.startDate || group.start || existing.startDate || todayIso()) : undefined,
    endDate: type !== 'list' && (group.endDate || group.end || existing.endDate) ? nbToIso(group.endDate || group.end || existing.endDate) : undefined,
    nextInvoiceDate: type !== 'list' ? nbToIso(group.nextInvoiceDate || group.startDate || group.start || existing.nextInvoiceDate || todayIso()) : undefined,
    automaticSending: type === 'list' ? false : Boolean(group.automaticSending ?? existing.automaticSending ?? (type === 'recurring')),
    status: ['pause', 'paused'].includes(group.status) ? 'pause' : ['ended', 'archived'].includes(group.status) ? 'ended' : 'active',
    customerId: type === 'recurring' ? (group.customerId || existing.customerId || group.invoiceTemplate?.customer?.id || undefined) : undefined,
    templateInvoiceId: type === 'recurring' ? (group.templateInvoiceId || existing.templateInvoiceId || undefined) : undefined,
    invoiceTemplate: type === 'recurring' ? (group.invoiceTemplate || existing.invoiceTemplate || undefined) : undefined,
    active: !['ended', 'archived'].includes(group.status) && group.active !== false && !group.archivedAt,
    createdAt: existing.createdAt || group.createdAt || now,
    updatedAt: now,
  });
};

const normalizeCustomerBillingGroupMember = (member = {}, existing = {}) => stripUndefinedDeep({
  ...existing,
  ...member,
  id: String(member.id || existing.id || (member.groupId && member.customerId ? `${member.groupId}:${member.customerId}` : createId('billing-member'))),
  groupId: String(member.groupId || existing.groupId || ''),
  customerId: String(member.customerId || existing.customerId || ''),
  active: member.active !== false,
  startDate: member.startDate || existing.startDate || todayIso(),
  endDate: member.endDate || existing.endDate || undefined,
  nextInvoiceDate: member.nextInvoiceDate || existing.nextInvoiceDate || undefined,
  removedAt: member.removedAt || undefined,
  createdAt: existing.createdAt || member.createdAt || nowIso(),
  updatedAt: nowIso(),
});

const getCustomerBillingModel = () => {
  const state = mergeState(runtime.appState);
  return clone({
    settings: state.settings,
    customers: safeArray(state.customers).filter((customer) => customer.status !== 'archived').map((customer) => mapCustomerToUi(customer, state)),
    groups: safeArray(state.customerBillingGroups),
    members: safeArray(state.customerBillingGroupMembers),
    invoiceRuns: safeArray(state.customerBillingGroupInvoiceRuns),
    emailRuns: safeArray(state.customerBillingGroupEmailRuns),
    invoices: safeArray(state.invoices),
    preservedSubscriptions: safeArray(state.customerSubscriptions),
  });
};

const saveCustomerBillingGroup = async (group) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  const existing = safeArray(mergeState(runtime.appState).customerBillingGroups).find((entry) => entry.id === group.id);
  const normalized = normalizeCustomerBillingGroup(group, existing);
  if (!normalized.name) throw new Error(normalized.type === 'list' ? t('sub.nameRequiredListError') : t('sub.nameRequiredAgreementError'));
  const result = await authorizedPost('recurringAgreementMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'upsert',
    group: normalized,
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  return result.group || normalized;
};

const recurringIntervalFromEditor = (value) => ({
  uke: 'weekly',
  '14dag': 'fourteen_days',
  maned: 'monthly',
  kvartal: 'quarterly',
  arlig: 'annual',
}[value] || (['weekly', 'fourteen_days', 'monthly', 'quarterly', 'semiannual', 'annual'].includes(value) ? value : 'monthly'));

const nextRecurringInvoiceDate = (iso, interval, anchorDayInput) => {
  const start = String(iso || todayIso()).slice(0, 10);
  if (interval === 'weekly') return addDays(start, 7);
  if (interval === 'fourteen_days') return addDays(start, 14);
  const date = new Date(`${start}T00:00:00Z`);
  const day = Math.min(31, Math.max(1, Math.round(num(anchorDayInput, date.getUTCDate()))));
  const delta = interval === 'quarterly' ? 3 : interval === 'semiannual' ? 6 : interval === 'annual' ? 12 : 1;
  const targetMonth = date.getUTCMonth() + delta;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonth / 12);
  const month = ((targetMonth % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(targetYear, month, Math.min(day, lastDay))).toISOString().slice(0, 10);
};

const saveRecurringInvoiceAgreement = async (invoice) => {
  if (!invoice?.id || !invoice?.recurring?.enabled) return null;
  const interval = recurringIntervalFromEditor(invoice.recurring.interval);
  const customerName = String(invoice.customer?.name || 'Kunde').trim();
  return saveCustomerBillingGroup({
    id: invoice.recurringAgreementId || `recurring-${invoice.id}`,
    type: 'recurring',
    name: String(invoice.description || invoice.lines?.[0]?.description || `Repeterende faktura til ${customerName}`).trim(),
    description: `Repeterende faktura til ${customerName}`,
    status: 'active',
    automaticSending: true,
    interval,
    dueDays: Math.max(0, Math.round(num(invoice.recurring.dueDays, 14))),
    startDate: invoice.date || todayIso(),
    anchorDay: Number(String(invoice.date || todayIso()).slice(8, 10)),
    nextInvoiceDate: nextRecurringInvoiceDate(invoice.date || todayIso(), interval, Number(String(invoice.date || todayIso()).slice(8, 10))),
    customerId: invoice.customerId || invoice.customer?.id || undefined,
    templateInvoiceId: invoice.id,
    invoiceTemplate: stripUndefinedDeep({
      customer: invoice.customer,
      customerId: invoice.customerId,
      reference: invoice.reference,
      ourReference: invoice.ourReference,
      theirReference: invoice.theirReference,
      customerReference: invoice.customerReference,
      description: invoice.description,
      comment: invoice.comment,
      bankAccount: invoice.bankAccount,
      bankAccountCode: invoice.bankAccountCode,
      lines: safeArray(invoice.lines),
      attachments: safeArray(invoice.attachments),
    }),
  });
};

const archiveCustomerBillingGroup = async (groupId) => {
  if (!groupId) throw new Error(t('svc.missingSubscriptionOrList'));
  const result = await authorizedPost('recurringAgreementMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'set-status',
    groupId,
    status: 'ended',
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  return getCustomerBillingModel();
};

const setCustomerBillingGroupStatus = async (groupId, status) => {
  if (!groupId) throw new Error(t('svc.missingSubscriptionOrList'));
  const result = await authorizedPost('recurringAgreementMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'set-status',
    groupId,
    status,
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  return result.group;
};

const saveCustomerBillingGroupMembers = async (groupId, customerIds = []) => {
  if (!groupId) throw new Error(t('svc.missingSubscriptionOrList'));
  const result = await authorizedPost('recurringAgreementMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'set-members',
    groupId,
    customerIds: Array.from(new Set(safeArray(customerIds).map((id) => String(id || '').trim()).filter(Boolean))),
  });
  if (result.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  return safeArray(result.state?.customerBillingGroupMembers).filter((member) => member.groupId === groupId);
};

const addCustomerBillingEmailRun = async (run = {}) => {
  let saved = null;
  await updateStateAndPersist((state) => {
    saved = stripUndefinedDeep({
      id: run.id || createId('billing-email-run'),
      groupId: run.groupId,
      subject: String(run.subject || '').trim(),
      message: String(run.message || '').trim(),
      count: Math.max(0, num(run.count)),
      sentCount: Math.max(0, num(run.sentCount)),
      failedCount: Math.max(0, num(run.failedCount)),
      recipients: safeArray(run.recipients),
      attachmentId: run.attachmentId,
      attachmentName: run.attachmentName,
      attachmentMimeType: run.attachmentMimeType,
      attachments: safeArray(run.attachments),
      attachmentCount: Math.max(0, num(run.attachmentCount || safeArray(run.attachments).length)),
      status: run.status || 'draft',
      createdAt: run.createdAt || nowIso(),
    });
    return {
      ...state,
      customerBillingGroupEmailRuns: [
        saved,
        ...safeArray(state.customerBillingGroupEmailRuns).filter((entry) => entry.id !== saved.id),
      ],
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return saved;
};

const uploadCustomerBillingEmailAttachment = async (file) => {
  if (!file) return null;
  if (!(file instanceof File)) throw new Error(t('svc.pickValidAttachment'));
  if (file.size > 10 * 1024 * 1024) throw new Error(t('svc.attachmentMax10'));
  await initFirebase();
  if (!runtime.user?.uid) throw new Error(t('svc.mustBeSignedIn2'));
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  const id = createId('billing-email-attachment');
  const cleanFileName = safeFilePart(file.name || 'vedlegg');
  // La under `users/{uid}/attachments/` fram til 2026-08-20. Den mappa er privat for
  // eieren, sa vedlegget var utilgjengelig for andre med tilgang til foretaket — og
  // ble liggende igjen nar foretaket ble slettet eller eksportert.
  const storagePath = `businesses/${runtime.activeBusinessId}/customerBillingGroupEmails/${id}-${cleanFileName}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: file.type || 'application/octet-stream',
    customMetadata: {
      source: 'customer-billing-group-email',
      originalName: cleanFileName,
    },
  });
  return {
    attachmentId: id,
    attachmentName: cleanFileName,
    attachmentMimeType: file.type || '',
    storagePath,
  };
};

const uploadCustomerBillingEmailAttachments = async (files = []) => {
  const fileList = safeArray(files).filter(Boolean);
  const uploaded = [];
  for (const file of fileList) {
    uploaded.push(await uploadCustomerBillingEmailAttachment(file));
  }
  return uploaded.filter(Boolean);
};

const sendCustomerBillingGroupEmail = async ({ groupId, subject, message, recipients = [], attachmentFile, attachmentFiles = [] } = {}) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));
  const state = mergeState(runtime.appState);
  const settings = state.settings || {};
  const uploadedAttachments = await uploadCustomerBillingEmailAttachments(
    safeArray(attachmentFiles).length ? attachmentFiles : (attachmentFile ? [attachmentFile] : []),
  );
  const attachment = uploadedAttachments[0] || null;
  const result = await authorizedPost('sendCustomerBillingGroupEmailHttp', stripUndefinedDeep({
    businessId: runtime.activeBusinessId,
    groupId,
    subject,
    message,
    recipients,
    settings: {
      email: settings.email || settings.contactEmail || runtime.userProfile?.email || runtime.user?.email || '',
      businessName: settings.businessName || settings.companyName || settings.name || '',
    },
    attachmentId: attachment?.attachmentId,
    attachmentName: attachment?.attachmentName,
    attachmentMimeType: attachment?.attachmentMimeType,
    attachments: uploadedAttachments,
  }));
  await addCustomerBillingEmailRun({
    groupId,
    subject,
    message,
    count: result.sentCount || 0,
    sentCount: result.sentCount || 0,
    failedCount: result.failedCount || 0,
    recipients: result.recipients || [],
    attachmentId: attachment?.attachmentId,
    attachmentName: attachment?.attachmentName,
    attachmentMimeType: attachment?.attachmentMimeType,
    attachments: uploadedAttachments,
    attachmentCount: uploadedAttachments.length,
    status: result.failedCount ? 'partial' : 'sent',
    createdAt: result.createdAt || nowIso(),
  });
  return result;
};

const saveCustomerRecord = async (uiCustomer, options = {}) => {
  let savedUiCustomer = null;
  await updateStateAndPersist((state) => {
    const existing = safeArray(state.customers).find((customer) => customer.id === uiCustomer.id);
    const backendCustomer = PlummeRegistryRecordPatch.apply('customer', mapUiCustomerToBackend(uiCustomer, existing), existing, uiCustomer, options.fields);
    const conflict = PlummeRegistryRecordPatch.findOrganizationConflict(safeArray(state.customers), backendCustomer, existing);
    if (conflict) throw new Error(t('svc.customerOrganizationExists', { name: conflict.name, number: conflict.customerNumber || conflict.organizationNumber }));
    const nextCustomers = [backendCustomer, ...safeArray(state.customers).filter((customer) => customer.id !== backendCustomer.id)];
    savedUiCustomer = mapCustomerToUi(backendCustomer, { ...state, customers: nextCustomers });
    return { ...state, customers: nextCustomers };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return savedUiCustomer;
};

/**
 * Legger en kontaktperson paa en kunde.
 *
 * Sendedialogen bruker den naar brukeren skriver inn en ny e-postadresse og vil
 * ta vare paa den. Egen funksjon i stedet for `saveCustomerRecord`, slik at
 * dialogen slipper aa sende hele kundeobjektet tilbake og risikere aa overskrive
 * felter noen andre har endret i mellomtiden.
 */
const addCustomerContactPerson = async ({ customerId, name, role, email, phone } = {}) => {
  const normalizedEmail = String(email || '').trim();
  if (!customerId) throw new Error(t('svc.missingCustomer'));
  if (!normalizedEmail) throw new Error(t('svc.contactNeedsEmail', 'Kontaktpersonen må ha en e-postadresse.'));

  let savedUiCustomer = null;
  await updateStateAndPersist((state) => {
    const customers = safeArray(state.customers);
    const index = customers.findIndex((customer) => customer.id === customerId);
    if (index < 0) throw new Error(t('svc.customerNotFound'));

    const existing = customers[index];
    const contacts = safeArray(existing.contactPersons);
    // Samme adresse to ganger ville gitt to like valg i mottakerlista.
    const duplicate = contacts.find((contact) =>
      String(contact.email || contact.epost || '').trim().toLowerCase() === normalizedEmail.toLowerCase());
    const nextContacts = duplicate
      ? contacts.map((contact) => contact === duplicate
        ? stripUndefinedDeep({ ...contact, name: String(name || '').trim() || contact.name, role: String(role || '').trim() || contact.role })
        : contact)
      : [...contacts, stripUndefinedDeep({
        id: `contact-${Date.now()}`,
        name: String(name || '').trim(),
        role: String(role || '').trim(),
        email: normalizedEmail,
        phone: String(phone || '').trim(),
      })];

    const nextCustomer = stripUndefinedDeep({ ...existing, contactPersons: nextContacts, updatedAt: nowIso() });
    const nextCustomers = [...customers];
    nextCustomers[index] = nextCustomer;
    savedUiCustomer = mapCustomerToUi(nextCustomer, { ...state, customers: nextCustomers });
    return { ...state, customers: nextCustomers };
  });

  window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return savedUiCustomer;
};

const archiveCustomerRecord = async (customerId) => {
  if (!customerId) throw new Error(t('svc.missingCustomer'));
  let archivedUiCustomer = null;
  await updateStateAndPersist((state) => {
    const customers = safeArray(state.customers);
    const index = customers.findIndex((customer) => customer.id === customerId);
    if (index < 0) throw new Error(t('svc.customerNotFound'));
    const nextCustomers = [...customers];
    const nextCustomer = stripUndefinedDeep({
      ...customers[index],
      status: 'archived',
      archivedAt: customers[index].archivedAt || nowIso(),
      updatedAt: nowIso(),
    });
    nextCustomers[index] = nextCustomer;
    archivedUiCustomer = mapCustomerToUi(nextCustomer, { ...state, customers: nextCustomers });
    return { ...state, customers: nextCustomers };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return archivedUiCustomer;
};

const importCustomerRecords = async (uiCustomers = []) => {
  let importedUiCustomers = [];
  await updateStateAndPersist((state) => {
    const existingCustomers = safeArray(state.customers);
    const byId = new Map(existingCustomers.map((customer) => [customer.id, customer]));
    const byOrg = new Map(existingCustomers.filter((customer) => customer.organizationNumber).map((customer) => [customer.organizationNumber, customer]));
    const byEmail = new Map(existingCustomers.filter((customer) => customer.email).map((customer) => [String(customer.email).toLowerCase(), customer]));
    const byName = new Map(existingCustomers.filter((customer) => customer.name).map((customer) => [String(customer.name).trim().toLowerCase(), customer]));
    const nextById = new Map(existingCustomers.map((customer) => [customer.id, customer]));
    safeArray(uiCustomers).forEach((uiCustomer, index) => {
      const org = String(uiCustomer.org || uiCustomer.organizationNumber || '').trim();
      const email = String(uiCustomer.email || '').trim().toLowerCase();
      const name = String(uiCustomer.name || '').trim();
      if (!name) return;
      const existing =
        byId.get(uiCustomer.id) ||
        (org ? byOrg.get(org) : null) ||
        (email ? byEmail.get(email) : null) ||
        byName.get(name.toLowerCase()) ||
        null;
      const backendCustomer = mapUiCustomerToBackend({
        ...uiCustomer,
        id: existing?.id || uiCustomer.id || createId(`customer-import-${index}`),
        name,
      }, existing || {});
      nextById.set(backendCustomer.id, backendCustomer);
      if (backendCustomer.organizationNumber) byOrg.set(backendCustomer.organizationNumber, backendCustomer);
      if (backendCustomer.email) byEmail.set(String(backendCustomer.email).toLowerCase(), backendCustomer);
      byName.set(String(backendCustomer.name).trim().toLowerCase(), backendCustomer);
    });
    const nextCustomers = Array.from(nextById.values());
    importedUiCustomers = nextCustomers
      .filter((customer) => customer.status !== 'archived')
      .map((customer) => mapCustomerToUi(customer, { ...state, customers: nextCustomers }));
    return { ...state, customers: nextCustomers };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return importedUiCustomers;
};

const saveSupplierRecord = async (uiSupplier, options = {}) => {
  let savedUiSupplier = null;
  await updateStateAndPersist((state) => {
    const existing = safeArray(state.suppliers).find((supplier) => supplier.id === uiSupplier.id);
    const backendSupplier = PlummeRegistryRecordPatch.apply('supplier', mapUiSupplierToBackend(uiSupplier, existing), existing, uiSupplier, options.fields);
    const conflict = PlummeRegistryRecordPatch.findOrganizationConflict(safeArray(state.suppliers), backendSupplier, existing);
    if (conflict) throw new Error(t('svc.supplierOrganizationExists', { name: conflict.name, number: conflict.supplierNumber || conflict.organizationNumber }));
    if (backendSupplier.supplierNumber && safeArray(state.suppliers).some((supplier) =>
      supplier.id !== backendSupplier.id && supplier.status !== 'archived' && supplier.supplierNumber === backendSupplier.supplierNumber
    )) {
      throw new Error(t('bam.billing.supplierNumberInUse', { number: backendSupplier.supplierNumber }));
    }
    const nextSuppliers = assignSupplierNumbers([backendSupplier, ...safeArray(state.suppliers).filter((supplier) => supplier.id !== backendSupplier.id)]);
    const savedBackendSupplier = nextSuppliers.find((supplier) => supplier.id === backendSupplier.id) || backendSupplier;
    savedUiSupplier = mapSupplierToUi(savedBackendSupplier, { ...state, suppliers: nextSuppliers });
    return { ...state, suppliers: nextSuppliers };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.suppliers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return savedUiSupplier;
};

const archiveSupplierRecord = async (supplierId) => {
  if (!supplierId) throw new Error(t('svc.missingSupplier'));
  let archivedUiSupplier = null;
  await updateStateAndPersist((state) => {
    const suppliers = safeArray(state.suppliers);
    const index = suppliers.findIndex((supplier) => supplier.id === supplierId);
    if (index < 0) throw new Error(t('svc.supplierNotFound'));
    const nextSuppliers = [...suppliers];
    const nextSupplier = stripUndefinedDeep({
      ...suppliers[index],
      status: 'archived',
      archivedAt: suppliers[index].archivedAt || nowIso(),
      updatedAt: nowIso(),
    });
    nextSuppliers[index] = nextSupplier;
    archivedUiSupplier = mapSupplierToUi(nextSupplier, { ...state, suppliers: nextSuppliers });
    return { ...state, suppliers: nextSuppliers };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.suppliers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return archivedUiSupplier;
};

const importSupplierRecords = async (uiSuppliers = []) => {
  let importedUiSuppliers = [];
  await updateStateAndPersist((state) => {
    const existingSuppliers = safeArray(state.suppliers);
    const byId = new Map(existingSuppliers.map((supplier) => [supplier.id, supplier]));
    const byOrg = new Map(existingSuppliers.filter((supplier) => supplier.organizationNumber).map((supplier) => [supplier.organizationNumber, supplier]));
    const byNumber = new Map(existingSuppliers.filter((supplier) => supplier.supplierNumber).map((supplier) => [supplier.supplierNumber, supplier]));
    const byEmail = new Map(existingSuppliers.filter((supplier) => supplier.email).map((supplier) => [String(supplier.email).toLowerCase(), supplier]));
    const byName = new Map(existingSuppliers.filter((supplier) => supplier.name).map((supplier) => [String(supplier.name).trim().toLowerCase(), supplier]));
    const nextById = new Map(existingSuppliers.map((supplier) => [supplier.id, supplier]));
    safeArray(uiSuppliers).forEach((uiSupplier, index) => {
      const org = String(uiSupplier.org || uiSupplier.organizationNumber || '').trim();
      const supplierNumber = cleanPartyNumber(uiSupplier.nr || uiSupplier.supplierNumber);
      const email = String(uiSupplier.email || '').trim().toLowerCase();
      const name = String(uiSupplier.name || '').trim();
      if (!name) return;
      const existing =
        byId.get(uiSupplier.id) ||
        (org ? byOrg.get(org) : null) ||
        (supplierNumber ? byNumber.get(supplierNumber) : null) ||
        (email ? byEmail.get(email) : null) ||
        byName.get(name.toLowerCase()) ||
        null;
      const backendSupplier = mapUiSupplierToBackend({
        ...uiSupplier,
        id: existing?.id || uiSupplier.id || createId(`supplier-import-${index}`),
        name,
      }, existing || {});
      nextById.set(backendSupplier.id, backendSupplier);
      if (backendSupplier.organizationNumber) byOrg.set(backendSupplier.organizationNumber, backendSupplier);
      if (backendSupplier.supplierNumber) byNumber.set(backendSupplier.supplierNumber, backendSupplier);
      if (backendSupplier.email) byEmail.set(String(backendSupplier.email).toLowerCase(), backendSupplier);
      byName.set(String(backendSupplier.name).trim().toLowerCase(), backendSupplier);
    });
    const nextSuppliers = assignSupplierNumbers(Array.from(nextById.values()));
    importedUiSuppliers = nextSuppliers
      .filter((supplier) => supplier.status !== 'archived')
      .map((supplier) => mapSupplierToUi(supplier, { ...state, suppliers: nextSuppliers }));
    return { ...state, suppliers: nextSuppliers };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.suppliers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return importedUiSuppliers;
};

const mutateSettingsData = async (input) => {
  const resource = input.resource;
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  const result = await authorizedPost('settingsDataMutationHttp', {
    businessId: runtime.activeBusinessId,
    ...input,
  });
  if (result?.state) {
    runtime.appState = mergeAppMutationState(result.state);
    emitRuntime();
  }
  if (resource && BACKEND_EVENTS[resource]) window.dispatchEvent(new Event(BACKEND_EVENTS[resource]));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result;
};

const deleteReferenceDataRecords = (resource, reason) => mutateSettingsData({ action: 'delete_reference_data', resource, reason });

const resetAllBusinessData = async (reason) => {
  if (!runtime.activeBusinessId) throw new Error(t('svc.pickBusiness'));
  const result = await authorizedPost('resetBusinessAppDataHttp', { businessId: runtime.activeBusinessId,
    confirmation: 'RESET_ALL_DATA', reason }, { requestTimeoutMs: 120000 });
  if (result?.resetState) { runtime.appState = mergeState(result.resetState); emitRuntime(); }
  Object.values(BACKEND_EVENTS).forEach((eventName) => window.dispatchEvent(new Event(eventName))); window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return result;
};

const importInvoiceRecords = async (invoices = []) => {
  const saved = [];
  for (const invoice of safeArray(invoices)) {
    const normalized = stripUndefinedDeep({
      ...invoice,
      id: invoice.id || createId('invoice-import'),
      kind: invoice.kind || 'invoice',
      status: invoice.status || 'draft',
      createdAt: invoice.createdAt || nowIso(),
      updatedAt: nowIso(),
    });
    saved.push(await upsertInvoice(normalized));
  }
  window.dispatchEvent(new Event(BACKEND_EVENTS.invoices));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return saved;
};

const importSaftMasterData = async (result = {}, options = {}) => {
  const yearKey = String(result.year || runtime.appState?.activeYear || new Date().getFullYear());
  const currentState = mergeState(runtime.appState);
  const hadOpeningBalance = Boolean(currentState.openingBalanceByYear?.[yearKey]);
  const openingBalanceUpdated = Boolean(result.openingBalance && (options.overwriteOpeningBalance || !hadOpeningBalance));
  let nextState = null;
  // openingBalanceByYear er server-eid og fjernes fra ordinære klientskrivinger.
  // Lagre derfor SAF-T-balansen gjennom samme auditerte Function som manuell IB.
  if (openingBalanceUpdated) {
    await saveOpeningBalance({ year: Number(yearKey), openingBalance: result.openingBalance });
  }
  await updateStateAndPersist((state) => {
    nextState = {
      ...state,
      chartAccounts: safeArray(result.chartAccounts),
      customers: safeArray(result.customers),
      suppliers: safeArray(result.suppliers),
    };
    return nextState;
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.customers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.suppliers));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return {
    year: Number(yearKey),
    chartAccounts: safeArray(nextState?.chartAccounts).length,
    customers: safeArray(nextState?.customers).length,
    suppliers: safeArray(nextState?.suppliers).length,
    openingBalanceUpdated,
  };
};

const exportSaftFinancialXml = () => {
  if (!window.PlummeSettingsDataTools?.exportSaftFinancialXml) throw new Error(t('svc.saftNotLoaded'));
  const state = mergeState(runtime.appState);
  const vouchers = getAccountingLedgerVouchersModel({ allYears: true });
  return window.PlummeSettingsDataTools.exportSaftFinancialXml(state, vouchers, { includePriorForPartyBalances: true });
};

const backendProductAccountCode = (value) => String(value || '').replace(/\D/g, '').slice(0, 5);
const backendProductStandardIncomeAccounts = (state) =>
  buildStandardChartAccounts(state?.settings)
    .filter((account) => String(account.code || '').startsWith('3'));
const backendProductAccountExists = (state, code) => {
  const cleanCode = String(code || '').trim();
  if (!cleanCode) return false;
  const settings = state?.settings || {};
  const accountCodes = [
    ...mergePlumme1ChartAccounts(state?.chartAccounts, settings).map((account) => account.code || account.accountNumber || account.id),
    ...safeArray(settings.bankAccounts).map((account) => account.ledgerAccountCode || account.ledger || account.accountCode),
    ...safeArray(settings.paymentAccounts).map((account) => account.ledgerAccountCode || account.ledger || account.accountCode || account.code),
    settings.bankAccountingAccount,
  ];
  return accountCodes.some((accountCode) => String(accountCode || '').trim() === cleanCode);
};
const ensureProductSalesAccount = (state, uiProduct = {}) => {
  const custom = uiProduct.customSalesAccount || uiProduct.salesAccountDraft;
  if (!custom) return state;
  const code = backendProductAccountCode(custom.code || custom.accountCode || uiProduct.salesAccountCode);
  const name = String(custom.name || custom.accountName || '').trim();
  const equivalentCode = backendProductAccountCode(custom.equivalentCode || custom.standardAccountCode || uiProduct.salesAccountEquivalentCode).slice(0, 4);
  if (!/^3\d{3,4}$/.test(code)) throw new Error(t('bam.billing.incomeAccountFormat'));
  if (!name) throw new Error(t('bam.billing.incomeAccountName'));
  const standardAccounts = backendProductStandardIncomeAccounts(state);
  if (!standardAccounts.some((account) => account.code === equivalentCode)) throw new Error(t('bam.billing.incomeAccountStandard'));
  if (backendProductAccountExists(state, code)) throw new Error(t('bam.billing.accountExists', { code }));
  const account = stripUndefinedDeep({
    id: `acct-${code}`,
    code,
    name,
    category: 'income',
    country: normalizeChartCountry(state?.settings),
    active: true,
    locked: false,
    custom: true,
    equivalentCode,
    standardAccountCode: equivalentCode,
    yearClosingStandardAccountCode: equivalentCode,
    mapsToAccountCode: equivalentCode,
    createdAt: nowIso(),
    createdBy: 'product',
  });
  return { ...state, chartAccounts: [...safeArray(state.chartAccounts), account] };
};

const saveProductRecord = async (uiProduct, options = {}) => {
  let savedUiProduct = null;
  await updateStateAndPersist((state) => {
    const nextState = ensureProductSalesAccount(state, uiProduct);
    const custom = uiProduct.customSalesAccount || uiProduct.salesAccountDraft;
    const accountPatch = custom ? {
      salesAccountCode: backendProductAccountCode(custom.code || custom.accountCode || uiProduct.salesAccountCode),
      salesAccountEquivalentCode: backendProductAccountCode(custom.equivalentCode || custom.standardAccountCode || uiProduct.salesAccountEquivalentCode).slice(0, 4),
    } : {};
    const existing = safeArray(nextState.products).find((product) => product.id === uiProduct.id);
    const backendProduct = PlummeRegistryRecordPatch.apply('product', mapUiProductToBackend({ ...uiProduct, ...accountPatch }, existing), existing, uiProduct, options.fields);
    const nextProducts = [backendProduct, ...safeArray(nextState.products).filter((product) => product.id !== backendProduct.id)];
    savedUiProduct = mapProductToUi(backendProduct);
    return { ...nextState, products: nextProducts };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.products));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return savedUiProduct;
};

const archiveProductRecord = async (productId) => {
  if (!productId) throw new Error(t('svc.missingProduct'));
  let archivedUiProduct = null;
  await updateStateAndPersist((state) => {
    const products = safeArray(state.products);
    const index = products.findIndex((product) => product.id === productId);
    if (index < 0) throw new Error(t('svc.productNotFound'));
    const nextProducts = [...products];
    const nextProduct = stripUndefinedDeep({
      ...products[index],
      status: 'archived',
      archivedAt: products[index].archivedAt || nowIso(),
      updatedAt: nowIso(),
    });
    nextProducts[index] = nextProduct;
    archivedUiProduct = mapProductToUi(nextProduct);
    return { ...state, products: nextProducts };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.products));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return archivedUiProduct;
};

const importProductRecords = async (uiProducts = []) => {
  let importedUiProducts = [];
  await updateStateAndPersist((state) => {
    const existingProducts = safeArray(state.products);
    const byId = new Map(existingProducts.map((product) => [product.id, product]));
    const byItemNumber = new Map(existingProducts.filter((product) => product.itemNumber).map((product) => [String(product.itemNumber).trim().toLowerCase(), product]));
    const byName = new Map(existingProducts.filter((product) => product.name).map((product) => [String(product.name).trim().toLowerCase(), product]));
    const nextById = new Map(existingProducts.map((product) => [product.id, product]));
    safeArray(uiProducts).forEach((uiProduct, index) => {
      const name = String(uiProduct.name || '').trim();
      if (!name) return;
      const itemNumber = String(uiProduct.nr || uiProduct.itemNumber || '').trim().toLowerCase();
      const existing =
        byId.get(uiProduct.id) ||
        (itemNumber ? byItemNumber.get(itemNumber) : null) ||
        byName.get(name.toLowerCase()) ||
        null;
      const backendProduct = mapUiProductToBackend({
        ...uiProduct,
        id: existing?.id || uiProduct.id || createId(`product-import-${index}`),
        name,
      }, existing || {});
      nextById.set(backendProduct.id, backendProduct);
      if (backendProduct.itemNumber) byItemNumber.set(String(backendProduct.itemNumber).trim().toLowerCase(), backendProduct);
      byName.set(String(backendProduct.name).trim().toLowerCase(), backendProduct);
    });
    const nextProducts = Array.from(nextById.values());
    importedUiProducts = nextProducts
      .filter((product) => product.status !== 'archived')
      .map(mapProductToUi);
    return { ...state, products: nextProducts };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.products));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return importedUiProducts;
};

const normalizeDocumentTextLine = (line = {}, existing = {}) => {
  const kind = line.kind === 'heading' || line.type === 'heading' ? 'heading' : 'text';
  const name = String(line.name || line.title || line.description || line.text || '').trim();
  const text = String(line.text || (line.name ? line.description : '') || '').trim();
  const nameStyle = line.nameStyle || { bold: line.bold === true || (!line.nameStyle && line.textStyle?.bold === true) || kind === 'heading', italic: line.italic === true || (!line.nameStyle && line.textStyle?.italic === true) };
  const textStyle = line.nameStyle ? (line.textStyle || { bold: false, italic: false }) : { bold: false, italic: false };
  return stripUndefinedDeep({
    ...existing,
    id: line.id || existing.id || createId('text-line'),
    name: name || text || (kind === 'heading' ? 'Overskrift' : 'Tekstlinje'),
    text,
    kind,
    nameStyle,
    textStyle,
    showDivider: line.showDivider !== false,
    bold: nameStyle.bold === true,
    italic: nameStyle.italic === true,
    sourceProductId: line.sourceProductId || existing.sourceProductId || undefined,
    status: line.status || existing.status || 'active',
    createdAt: existing.createdAt || nowIso(),
    updatedAt: nowIso(),
  });
};

const saveDocumentTextLineRecord = async (line) => {
  let saved = null;
  await updateStateAndPersist((state) => {
    const existing = safeArray(state.documentTextLines).find((entry) => entry.id === line.id);
    saved = normalizeDocumentTextLine(line, existing);
    return { ...state, documentTextLines: [saved, ...safeArray(state.documentTextLines).filter((entry) => entry.id !== saved.id)] };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.documentTextLines));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return saved;
};

const archiveDocumentTextLineRecord = async (lineId) => {
  if (!lineId) throw new Error(t('bam.billing.missingTextLine'));
  let archived = null;
  await updateStateAndPersist((state) => {
    const rows = safeArray(state.documentTextLines);
    const index = rows.findIndex((entry) => entry.id === lineId);
    if (index < 0) throw new Error(t('bam.billing.textLineNotFound'));
    const next = [...rows];
    archived = stripUndefinedDeep({ ...next[index], status: 'archived', archivedAt: next[index].archivedAt || nowIso(), updatedAt: nowIso() });
    next[index] = archived;
    return { ...state, documentTextLines: next };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.documentTextLines));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return archived;
};
